import { Link } from "react-router-dom";
import { ArrowRight, Sun, Heart, Sparkles, ChefHat, Compass } from "lucide-react";
import { Button } from "../components/ui/button";
import { Overline } from "../components/Overline";
import { Seo } from "../components/Seo";

export default function Retreats() {
  return (
    <div data-testid="page-retreats" className="bg-[#faf9f6]">
      <Seo title="Retreats" description="A summer retreat in Italy for young women aged 16 to 20. Cultural immersion, body literacy and the life skills no one taught you. A big sister's club, in a beautiful Italian villa." path="/retreats" />
      {/* HERO */}
      <section className="relative overflow-hidden sheen-sage">
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#cbdad6]/40 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-[#f3eee8]/60 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <Overline>Retreats</Overline>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.04] font-normal text-[#333] mt-5">
                A week to learn
                <br />
                <span className="italic">what school never taught.</span>
              </h1>
              <p className="mt-8 text-lg md:text-xl text-[#3d3d3d] font-normal leading-relaxed max-w-xl">
                A summer retreat for young women aged 16 to 20. One week of
                cultural immersion, real food, body literacy and the life
                skills no one taught you in classroom Latin. Held in a private
                villa in Italy, with a small group, with the kind of care a
                mother would want for her own daughter.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="retreats-cta-primary">
                  <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 py-6 h-auto text-base font-medium tracking-wide">
                    Register your interest
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="arch-image aspect-[4/5] bg-[#cbdad6]">
                <img
                  src="/brand/retreat-italy.png"
                  alt="Young women at a sunlit Italian retreat table"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-20 md:py-28 max-w-5xl mx-auto px-6 md:px-12">
        <Overline>The idea</Overline>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight font-normal text-[#333] mt-5">
          A cultural week with meaning,
          <br />
          <span className="italic">in a beautiful Italian villa.</span>
        </h2>
        <div className="mt-10 space-y-6 text-lg text-[#3d3d3d] font-normal leading-relaxed">
          <p>
            School is becoming responsible for covering an ever-increasing
            scope of education. PSHE, wellbeing, life-skills programmes are
            all getting better. But there are still depths and nuances it
            doesn't have time for, and there are still some conversations a
            young woman finds easier with a slightly older one than with a
            teacher or a parent.
          </p>
          <p>
            This retreat is that conversation, given a whole week, in a fun,
            enriching environment. A small group of young women, two
            practitioners, a beautiful villa, and the kind of slow
            Mediterranean days that make space for real things to be said.
            Cooking. Painting. Walking. Reading.
          </p>
          <p>
            Real talk on bodies, cycles, fertility, relationships and the
            cultural pressures that shape young women's choices. There isn't
            a woman today I know that didn't wish she had known certain
            things about relationships, dating and her body when she was
            younger.
          </p>
          <p>
            It's a bit like a big sister's club. Cool, comfortable, honest,
            and genuinely helpful. Your daughter leaves with more assurance
            in how to handle and make the choices which are bound to come,
            while you as parents can feel secure that your daughter has the
            tools and confidence to assert boundaries and make informed
            decisions, in the medical room and on the dating scene.
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-16 md:py-20 bg-[#f3eee8]/60 border-y border-[#e5e0d8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Overline>What's included</Overline>
          <h3 className="font-serif text-3xl md:text-4xl mt-4 text-[#333] font-normal tracking-tight max-w-3xl">
            Seven days, one villa, one small group of young women.
          </h3>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: ChefHat, title: "Cultural immersion", body: "Italian cooking classes, market trips, regional excursions, art and writing workshops led by local mentors." },
              { icon: Heart, title: "Body literacy", body: "Age-appropriate teaching on cycles, fertility, contraception choices, what 'normal' actually looks like, and how to advocate for yourself in a medical room." },
              { icon: Compass, title: "Life skills", body: "Relationships, consent, money basics, leaving home, learning to cook real meals you'd actually want to eat." },
              { icon: Sun, title: "Real rest", body: "Sleep that begins before midnight. Mornings in natural light. Time outside. Time off screens." },
              { icon: Sparkles, title: "Small group", body: "Maximum 10 young women per retreat. Two practitioners on site. Genuine attention, never crowded." },
              { icon: Heart, title: "Parents informed", body: "A pre-retreat parent call, a post-retreat written debrief, and continued resources for the conversation to keep going at home." },
            ].map((c) => (
              <div
                key={c.title}
                data-testid={`retreat-pillar-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white border border-[#e5e0d8] rounded-3xl p-7"
              >
                <c.icon className="text-[#5fa292]" size={24} />
                <h4 className="font-serif text-xl text-[#333] mt-4 font-normal tracking-tight">
                  {c.title}
                </h4>
                <p className="mt-3 text-[#3d3d3d] font-normal leading-relaxed text-base">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <Overline>Practical details</Overline>
            <div className="mt-8 space-y-6">
              {[
                { k: "Ages", v: "16 to 20" },
                { k: "Group size", v: "Maximum 10 young women" },
                { k: "Duration", v: "7 days, 6 nights" },
                { k: "Location", v: "Private villa, Italy" },
                { k: "Dates", v: "Summer 2026 - dates announced soon" },
                { k: "Investment", v: "By application. Full pricing on request." },
              ].map((d) => (
                <div key={d.k} className="grid grid-cols-3 gap-4 pb-4 border-b border-[#e5e0d8]">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5fa292] col-span-1">
                    {d.k}
                  </p>
                  <p className="font-serif text-lg text-[#333] col-span-2">
                    {d.v}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#cbdad6]/40 border border-[#e5e0d8] rounded-3xl p-10 md:p-12 flex flex-col">
            <Overline>For parents</Overline>
            <h3 className="font-serif text-3xl md:text-4xl mt-4 text-[#333] font-normal tracking-tight leading-tight">
              The retreat you would have wanted for yourself.
            </h3>
            <p className="mt-5 text-[#3d3d3d] font-normal leading-relaxed flex-1">
              If you are a mother or father with a daughter in this age range,
              and you want her to have a week of genuine learning, real rest
              and grown-up conversation with women who will tell her the truth
              about her own body, this is for her. Register your interest and
              I will be in touch personally with the next dates, the parent
              call and the application.
            </p>
            <Link to="/contact" className="inline-block mt-8" data-testid="retreats-cta-secondary">
              <Button className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 h-12 w-fit">
                Register your interest
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* THE ANTI-WELLNESS RETREAT */}
      <section
        data-testid="retreat-anti-wellness"
        className="relative py-24 md:py-32 bg-[#2b2f2c] text-[#faf9f6] overflow-hidden"
      >
        <div className="absolute top-16 right-8 w-40 h-40 rounded-full bg-[#5fa292]/10 blur-2xl pointer-events-none" aria-hidden />
        <div className="absolute bottom-20 left-8 w-52 h-52 rounded-full bg-[#f3eee8]/5 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#cbdad6] font-medium">
            Coming soon · a second retreat
          </p>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-normal mt-6">
            The Anti-Wellness
            <br />
            <span className="italic text-[#cbdad6]">Retreat.</span>
          </h2>
          <p className="mt-8 text-xl md:text-2xl text-[#e8e4dc] font-normal max-w-3xl leading-snug">
            A health retreat for people who don't do wellness culture.
          </p>

          <div className="mt-12 grid md:grid-cols-2 gap-x-16 gap-y-6 max-w-4xl">
            <div className="space-y-4">
              <p className="text-lg text-[#e8e4dc] font-normal leading-relaxed">
                No leggings required.
              </p>
              <p className="text-lg text-[#e8e4dc] font-normal leading-relaxed">
                No influencer aesthetic.
              </p>
              <p className="text-lg text-[#e8e4dc] font-normal leading-relaxed">
                No pilates in designer studios.
              </p>
            </div>
            <div>
              <p className="text-lg text-[#e8e4dc] font-normal leading-relaxed">
                Just real health as a practice, that will add quality and
                years to your life.
              </p>
            </div>
          </div>

          <div className="mt-16 border-t border-[#4a5049] pt-12">
            <p className="text-xs uppercase tracking-[0.25em] text-[#cbdad6] mb-8">
              What we cover
            </p>
            <div className="grid md:grid-cols-2 gap-x-16 gap-y-5 max-w-4xl">
              {[
                "Breathing properly, without breathwork theatre",
                "Sunlight and light hygiene",
                "Neuromechanics of strength and mobility for modern lifestyles and ageing populations",
                "A functioning nervous system, without the woowoo",
                "Maintaining muscle and bone mass",
                "Being able to get up off the floor",
                "Understanding your own physiology",
                "Spending time outdoors",
                "Meaningful social connection",
              ].map((item, i) => (
                <p
                  key={i}
                  data-testid={`anti-wellness-pillar-${i}`}
                  className="flex gap-4 text-[15px] md:text-base text-[#e8e4dc] font-normal leading-relaxed"
                >
                  <span className="font-serif italic text-[#cbdad6] text-lg leading-none pt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{item}</span>
                </p>
              ))}
            </div>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Link to="/contact" data-testid="anti-wellness-cta">
              <Button className="bg-[#cbdad6] text-[#2b2f2c] hover:bg-white rounded-full px-8 h-12 font-medium">
                Register your interest
              </Button>
            </Link>
            <p className="text-sm text-[#a8ada6] font-normal">
              Dates and location announced soon. Small groups. Older welcome.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
